import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchData, joinDataArrays } from '../utils/functions';
import { itemShell } from '../utils/form-data';
import { createOrUpdateItem } from './utils/functions';
import SourceFields from './SourceFields';
import TagOption from '../utils/item-search/TagOption';
import DescriptionField from './DescriptionField';
import FinancialFields from './FinancialFields';
import ImageUpload from './ImageUpload';
import StatusOption from './StatusOption';

export default function AccessionPage() {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queries = {
    sourceArea: useQuery('sourceAreaData', () => fetchData('source-area')),
    sourceCity: useQuery('sourceCityData', () => fetchData('source-city')),
    sourceState: useQuery('sourceStateData', () => fetchData('source-state')),
    sourceType: useQuery('sourceTypeData', () => fetchData('source-type')),
    source: useQuery('sourceData', () => fetchData('source')),
    tagTagType: useQuery('tagTagTypeData', () => fetchData('tag-tag-type')),
    tagType: useQuery('tagTypeData', () => fetchData('tag-type')),
    tag: useQuery('tagData', () => fetchData('tag')),
    salesChannel: useQuery('salesChannelData', () => fetchData('sales-channel')),
    status: useQuery('statusData', () => fetchData('status')),
    location: useQuery('locationData', () => fetchData('location'))
  };

  const { itemId } = useParams();
  const navigate = useNavigate();
  const treasureQuery = useQuery(
    ['treasureData', itemId],
    () => fetchData(`treasure/${itemId}`),
    {
      enabled: !!itemId,
    }
  );
  const [itemFormData, setItemFormData] = useState(itemShell);
  const [originalItemData, setOriginalItemData] = useState();
  const [completeSourceData, setCompleteSourceData] = useState([]);
  const [completeTagData, setCompleteTagData] = useState(null);
  const [submitButtonLabel, setSubmitButtonLabel] = useState('Save');
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (treasureQuery.data) {
      console.log('originalItemData', treasureQuery.data[0])
      setItemFormData(treasureQuery.data[0]);
      setOriginalItemData(treasureQuery.data[0]);
    } else {
      setItemFormData(itemShell);
      setOriginalItemData(undefined);
    }
  }, [treasureQuery.data]);

  useEffect(() => {
    if (Object.values(queries).every(query => query.data !== undefined)) {
      const { sourceArea, sourceCity, sourceState, sourceType, source, tag, tagType, tagTagType } = queries;
      const areaWithCity = joinDataArrays(sourceArea.data, 'city_id', sourceCity.data, 'id', { city: 'city' });
      const completeArea = joinDataArrays(areaWithCity, 'state_id', sourceState.data, 'id', { state: 'state' });
      const sourceWithArea = joinDataArrays(source.data, 'area_id', completeArea, 'id', { city: 'city', state: 'state', city_id: 'city_id', state_id: 'state_id' }, 'area');
      const completeSources = joinDataArrays(sourceWithArea, 'type_id', sourceType.data, 'id', { type: 'type' });
      const tagTagTypesWithTagTypeNames = joinDataArrays(tagTagType.data, 'tag_type_id', tagType.data, 'id', { type: 'type' });
      const tagsWithTagTypes = joinDataArrays(tag.data, 'id', tagTagTypesWithTagTypeNames, 'tag_id', { id: 'tag_type_id', type: 'type' }, 'tagTypes');
      setCompleteSourceData(completeSources);
      setCompleteTagData(tagsWithTagTypes);
    }
  }, Object.values(queries).map(query => query.data));
  
  useEffect(() => {
    if (itemFormData) {
      console.log("Item Form Data:", itemFormData);
      const { location, shipping_cost, sale_price } = itemFormData;
      const { status, sales_channel, review_date } = itemFormData.statuses[itemFormData.statuses.length - 1];
      
      if (status.name === 'exclusive' && sales_channel.channel && review_date && location.name) {
        setSubmitButtonLabel(itemId ? 'Save Changes' : 'Save Item With Exclusive Status');
      } else if (status.name === 'listed' && sales_channel.channel && review_date && location.name) {
        setSubmitButtonLabel(itemId ? 'Save Changes' : 'Save Item With Listed Status');
      } else if (status.name === 'sold' && sales_channel.channel && shipping_cost && sale_price) {
        setSubmitButtonLabel(itemId ? 'Save Changes' : 'Save Item With Sold Status');
      } else if (status.name === 'shelved' && review_date && location.name) {
        setSubmitButtonLabel(itemId ? 'Save Changes' : 'Save Item With Shelved Status');
      } else if (status.name === 'removed') {
        setSubmitButtonLabel(itemId ? 'Save Changes' : 'Save Item With Removed Status');
      } else {
        setSubmitButtonLabel('Save');
      }
    }
  }, [itemFormData, itemId]);
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    console.log('originalItemData', originalItemData)
    await createOrUpdateItem(
      itemFormData,
      setItemFormData,
      error,
      setError,
      originalItemData
    );
    if (originalItemData) {
      navigate(-1);
    }
    queries.tagTagType.refetch();
    queries.tagType.refetch();
    queries.tag.refetch();
  };

  return (
    <div>
      <h1>{itemId ? 'Edit Item' : 'Create New Item'}</h1>
      <SourceFields
        itemFormData={itemFormData}
        setItemFormData={setItemFormData}
        completeSourceData={completeSourceData}
        sourceTypeData={queries.sourceType.data}
        sourceCityData={queries.sourceCity.data}
        sourceStateData={queries.sourceState.data}
      />
      <br />
      {completeTagData && queries.tagType.data && (
        <TagOption
          itemFormData={itemFormData}
          setItemFormData={setItemFormData}
          tagTypeData={queries.tagType.data}
          completeTagData={completeTagData}
        />
      )}
      <br/>
      <DescriptionField itemFormData={itemFormData} setItemFormData={setItemFormData} />
      <FinancialFields itemFormData={itemFormData} setItemFormData={setItemFormData} />
      <ImageUpload 
        itemFormData={itemFormData}
        setItemFormData={setItemFormData}
        originalItemData={originalItemData}
      />
      {!itemId &&(
        <StatusOption
          itemFormData={itemFormData}
          setItemFormData={setItemFormData}
          statusData={queries.status.data}
          salesChannelData={queries.salesChannel.data}
          locationData={queries.location.data}
          itemId={Number(itemId)}
        />
      )}
      <br />
      <button onClick={handleFormSubmit} disabled={submitButtonLabel === 'Save' || !itemFormData.featured_image}>{submitButtonLabel}</button>
      {error && (
        <div style={{ color: 'red' }}>
          {error}
        </div>
      )}
    </div>
  );
}
